import Chip from "@mui/material/Chip";
import { useTranslation } from "i18n-fe";

// card #1662 — status/confidence display for a catalog def's fvdBinding
// (docs/score/schema/catalog-def.target.schema.json), computed by
// score-common's catalog/confidence.js and seeded at ingest -- never
// hand-authored; only a human review verdict (#1658's PATCH /catalog/:id,
// applied in bulk by #1660's review-export tool) ever promotes status to
// "confirmed". Mirrors SheetStateChip: semantic MUI Chip colors only (NEVER
// brand `primary` here -- it's dark grey and disappears on a dark
// background), static i18n labelKeys (i18n-fe requires literal keys, no
// interpolation).
export const BINDING_STATUS_DISPLAY = {
  confirmed: { color: "success", labelKey: "testDefs.binding.status.confirmed" },
  inferred: { color: "warning", labelKey: "testDefs.binding.status.inferred" },
  unmapped: { color: "default", labelKey: "testDefs.binding.status.unmapped" },
};

export const BINDING_CONFIDENCE_DISPLAY = {
  high: { color: "success", labelKey: "testDefs.binding.confidence.high" },
  medium: { color: "warning", labelKey: "testDefs.binding.confidence.medium" },
  low: { color: "error", labelKey: "testDefs.binding.confidence.low" },
};

// card #1662 — signal codes fired by score-common's catalog/confidence.js
// (packages/score-common/src/catalog/confidence.js), stored on fvdBinding as
// the compact code list (e.g. "year-mismatch"). Static lookup table, same
// pattern as BINDING_STATUS_DISPLAY above -- i18n-fe requires literal keys,
// no dynamically-constructed ones. A code not in this table (a future signal
// added to confidence.js before this table catches up) falls back to the raw
// code rather than disappearing or throwing, so it stays visible even if
// unlabeled.
export const BINDING_SIGNAL_LABEL_KEYS = {
  unmapped: "testDefs.binding.signals.unmapped",
  split: "testDefs.binding.signals.split",
  collision: "testDefs.binding.signals.collision",
  "year-match": "testDefs.binding.signals.yearMatch",
  "year-mismatch": "testDefs.binding.signals.yearMismatch",
  "name-exact": "testDefs.binding.signals.nameExact",
  "name-close": "testDefs.binding.signals.nameClose",
  "name-divergent": "testDefs.binding.signals.nameDivergent",
  "no-evidence": "testDefs.binding.signals.noEvidence",
};

export function BindingStatusChip({ status, size = "small", ...props }) {
  const { t } = useTranslation("scoreadmin");
  const display = BINDING_STATUS_DISPLAY[status] ?? BINDING_STATUS_DISPLAY.unmapped;
  return (
    <Chip
      size={size}
      color={display.color}
      label={t(display.labelKey)}
      variant={status === "confirmed" ? "filled" : "outlined"}
      data-testid="fvd-binding-status-chip"
      {...props}
    />
  );
}

export function BindingConfidenceChip({ confidence, size = "small", ...props }) {
  const { t } = useTranslation("scoreadmin");
  const display = BINDING_CONFIDENCE_DISPLAY[confidence];
  if (!display) return null;
  return (
    <Chip
      size={size}
      color={display.color}
      variant="outlined"
      label={t(display.labelKey)}
      data-testid="fvd-binding-confidence-chip"
      {...props}
    />
  );
}

// card #1662 — one evidence chip per fired signal code. Falls back to the
// raw code (never a leaked-looking blank) when the code isn't in the table.
export function BindingSignalChip({ code, size = "small", ...props }) {
  const { t } = useTranslation("scoreadmin");
  const labelKey = BINDING_SIGNAL_LABEL_KEYS[code];
  return (
    <Chip
      size={size}
      variant="outlined"
      label={labelKey ? t(labelKey) : code}
      data-testid="fvd-binding-signal-chip"
      {...props}
    />
  );
}
